import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";

function SortableTile({ img, onRemove, index }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: img.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`sortable-tile-${index}`}
      className="group relative aspect-square rounded-xl overflow-hidden bg-slate-100 sb-hairline"
    >
      <img
        src={img.url}
        alt=""
        className="w-full h-full object-cover pointer-events-none select-none"
        draggable={false}
      />
      <div className="absolute top-1 left-1 bg-black/70 text-white text-[10px] font-mono px-1.5 py-0.5 rounded-md pointer-events-none">
        {index + 1}
      </div>
      <button
        type="button"
        onClick={() => onRemove(img.id)}
        data-testid={`sortable-remove-${index}`}
        className="absolute top-1 right-1 bg-black/80 text-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Remove"
      >
        <X size={12} strokeWidth={2} />
      </button>
      <div
        {...attributes}
        {...listeners}
        className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 text-white flex items-center justify-center cursor-grab active:cursor-grabbing"
      >
        <GripVertical size={12} />
      </div>
    </div>
  );
}

export default function SortablePhotoGrid({ images, onReorder, onRemove }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = images.findIndex((i) => i.id === active.id);
    const newIndex = images.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(images, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={images.map((i) => i.id)} strategy={rectSortingStrategy}>
        <div
          data-testid="sortable-photo-grid"
          className="grid grid-cols-3 sm:grid-cols-4 gap-2"
        >
          {images.map((img, idx) => (
            <SortableTile
              key={img.id}
              img={img}
              index={idx}
              onRemove={onRemove}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
